/**
 * The corridor: where national freight enters a district, and what it costs to be
 * on the way to somewhere else.
 *
 * `DESIGN-GOVERNORS.md` §5, under *Freight and interchanges*:
 *
 * > District maps gain **interchanges**: edge structures (2 cells) where national
 * > freight enters and leaves the map. […] Consequences that fall out for free:
 * >
 * > - A district on a trade corridor hosts **transit traffic** it didn't generate:
 * >   noise, congestion, road wear, and ant deaths from trucks it never ordered.
 * >   Tolls on transit are a Charter option.
 * > - Freight capacity of a polity is a *measured* quantity: routes × road quality
 * >   × congestion.
 *
 * That first sentence of §5 is about facilities — *"Facilities requiring freight
 * (factories, plants, logistics hubs) need a paved route from an interchange"* —
 * and all three of those facilities are unbuilt, because each needs a flow this
 * district does not have. So what is built here is the **second** sentence: the
 * transit corridor, which needs no factory, no goods stock and no world market. It
 * needs two interchanges and a road between them.
 *
 * The same choice `FacilityManager` made about the data centre, for the same
 * reason: build the part whose inputs exist, and say plainly which part that is.
 *
 * ## What a leg is
 *
 * One truck, entering at one interchange and leaving at another. The polity offers
 * so many per budget cycle (`setOffered`); this class decides which pair each one
 * runs between and hands `SurfaceSim` a from and a to. Everything after that is
 * `CarManager`'s: a truck is a vehicle on the district's roads, at the district's
 * road quality, in the district's congestion, and it kills what it drives over on
 * exactly the terms every other vehicle has since long before there was a polity.
 *
 * Nothing was added to make that last part happen, which is the whole argument for
 * building freight as trucks rather than as a number: §4's *"the layout is the
 * ledger"* only means anything if the freight is physically on the map.
 *
 * ## What this class cannot see
 *
 * The ground, and a hash type — the same import list `MercyManager` has, for the
 * sharper of the two reasons. A leg is dispatched from a pair of coordinates to a
 * pair of coordinates; who is standing on the road between them, what a truck did
 * on the way, and what is under the tarmac are three questions this file cannot
 * ask. It counts legs and it counts dollars.
 *
 * `tests/purity.test.ts` pins the list. The account may not see a household
 * (ADR-021 D137); the corridor may not see what it runs over, which is the same
 * law read at the level where it actually bites.
 *
 * See `docs/freight.md`.
 */

import { Canonical } from '../hashState';
import { CellState, GridCoord, TerrainGrid } from './TerrainGrid';

/**
 * What a corridor is worth and what it costs. World parameters, not state.
 *
 * §5 and §6 price none of this: *"Tolls (if Charter allows) | Host districts along
 * freight routes | Per truck-leg"* is the whole of the specification, so all three
 * numbers are chosen, and what they are chosen against is the district's own
 * measured figures — a season is about $16,200 of tax across ninety cycles, or
 * $180 a cycle, and the shipped road budget is $50 of that.
 *
 * See `docs/freight.md#calibration` for the ladder both ends were measured on.
 */
export interface FreightConfig {
  /**
   * Dollars to build one two-cell interchange.
   *
   * Between a hedgerow and a covenant: an interchange is a purchase from outside
   * the district, so under ADR-021's rule it lands on the road budget until
   * receipts have covered it — about four cycles of a grown district's, or a month
   * of a tithed one's. A corridor should cost a mayor a visible month.
   */
  INTERCHANGE_COST: number;
  /**
   * Dollars the polity pays this district per completed truck-leg.
   *
   * §6's revenue split, fourth stream. Statutory rather than the mayor's, and the
   * reason is that a toll a mayor set would not be a price: there is one district
   * and no route around it, so an elastic-demand story would be invented and an
   * inelastic one would be a printing press. §18's `set_tolls(rate)` is a Charter
   * verb and waits for a Charter and for somewhere else for the freight to go.
   */
  TOLL_PER_LEG: number;
  /**
   * Points of road quality one completed leg takes off.
   *
   * §5's *"road wear"*, and the counterweight that makes a corridor a decision
   * rather than free money. The treasury already decays the roads by three points
   * a cycle and buys them back with the maintenance budget; a truck is the first
   * thing in this district that decays them faster than a house does.
   */
  WEAR_PER_LEG: number;
}

export const DEFAULT_FREIGHT_CONFIG: FreightConfig = {
  INTERCHANGE_COST: 600,
  TOLL_PER_LEG: 14,
  WEAR_PER_LEG: 0.5,
};

/** Which way the second cell of an interchange lies from the first. */
export type InterchangeAxis = 'h' | 'v';

/** One interchange: two cells of road, and an id assigned in build order. */
export interface Interchange {
  /** 1 for the first one a district builds. Ids are never reused. */
  id: number;
  x: number;
  y: number;
  axis: InterchangeAxis;
}

/** Why an interchange was not built. Information for the caller, not an error. */
export type FreightRefusal =
  /** Either cell is off the map. A structure does not straddle the seam. */
  | { ok: false; reason: 'off-map' }
  /** Either cell is not road. An interchange is built on the district's pavement. */
  | { ok: false; reason: 'unpaved' }
  /** Either cell already belongs to an interchange. */
  | { ok: false; reason: 'occupied' };

/** What an interchange cost, or why there was not one. */
export type FreightResult = { ok: true; cost: number } | FreightRefusal;

/** One truck-leg, as `SurfaceSim` needs it to dispatch a vehicle. */
export interface FreightLeg {
  /** Assigned in dispatch order, never reused. Names the leg in the out-tray. */
  id: number;
  /** The interchange it enters at. */
  from: GridCoord;
  /** The interchange it leaves at. */
  to: GridCoord;
}

/**
 * What the district's corridor has come to.
 *
 * Monotone sums, except `interchanges`, which is a level and only rises. A
 * season's freight is a subtraction over two Chronicle lines, exactly as a
 * season's energy is.
 */
export interface FreightLedger {
  /** Interchanges standing. Never falls: there is no verb that removes one. */
  interchanges: number;
  /** Dollars spent building them, lifetime. */
  spent: number;
  /** Whole truck-legs the polity has offered this district, lifetime. */
  legsOffered: number;
  /** Legs that became a truck on the map, lifetime. */
  legsDispatched: number;
  /** Trucks that reached the far interchange, lifetime. What a toll is paid on. */
  legsCompleted: number;
  /**
   * Trucks that entered and gave up, lifetime.
   *
   * A route that does not connect, or one too slow to finish inside the mission
   * timeout. The polity is not paid for it and the district is not paid for it,
   * and §5's *"routes × road quality × congestion"* is this number against
   * `legsDispatched` — freight capacity, measured rather than declared.
   */
  legsAbandoned: number;
  /**
   * Legs offered that never became a truck, lifetime.
   *
   * A district with fewer than two interchanges has nowhere for freight to go, and
   * one whose gates are blocked by parked cars has no room for it to arrive. Both
   * are the corridor failing before it starts, and neither is the truck's fault.
   */
  legsLapsed: number;
  /** Dollars of toll earned, lifetime. */
  tollsEarned: number;
  /** Points of road quality transit has worn away, lifetime. */
  roadWear: number;
}

/** Everything the corridor is. See `FreightManager.save()`. */
export interface FreightSnapshot {
  interchanges: Interchange[];
  nextId: number;
  nextLegId: number;
  offered: number;
  pending: number;
  nextPair: number;
  settledMark: number;
  ledger: FreightLedger;
}

/** What a settled cycle's completed legs come to. See `haulage`. */
export interface Haulage {
  /** Legs completed since the last one booked. */
  legs: number;
  /** Dollars of toll they earned. */
  tolls: number;
  /** Points of road quality they wore away. */
  wear: number;
}

function emptyLedger(): FreightLedger {
  return {
    interchanges: 0,
    spent: 0,
    legsOffered: 0,
    legsDispatched: 0,
    legsCompleted: 0,
    legsAbandoned: 0,
    legsLapsed: 0,
    tollsEarned: 0,
    roadWear: 0,
  };
}

/** The second cell of an interchange, given the first and the axis. */
export function interchangeCells(spot: {
  x: number;
  y: number;
  axis: InterchangeAxis;
}): [GridCoord, GridCoord] {
  return [
    { x: spot.x, y: spot.y },
    spot.axis === 'h' ? { x: spot.x + 1, y: spot.y } : { x: spot.x, y: spot.y + 1 },
  ];
}

/**
 * The district's interchanges and the freight that crosses them.
 *
 * Owned by `SurfaceSim`, settled on the same budget cycle as the flows, and
 * snapshotted inside the surface like every other manager here.
 */
export class FreightManager {
  private config: FreightConfig;

  private readonly interchanges: Interchange[] = [];
  private nextId = 1;
  private nextLegId = 1;

  /**
   * Truck-legs the polity offers this district per budget cycle.
   *
   * Zero until somebody says otherwise, and there is nobody: a district with no
   * polity above it is on nobody's corridor, so it sits at the bottom of the dial
   * rather than at some invented default. Same standing as the tithe rate, and the
   * same reason every recorded run of this district has an empty freight block.
   *
   * A *dial* rather than a verb, unlike the tithe, and the test is ADR-021 D135's:
   * an allocation re-decided from scratch every cycle can be a scalar, because a
   * district restored from a snapshot re-derives what it gets. A tithe is charged
   * once against a season and has to be in the command log. Freight offered per
   * cycle is the first kind. See `setOffered`.
   */
  private offered = 0;

  /**
   * The fraction of a leg carried over from the last cycle.
   *
   * Freight is offered as a rate and dispatched as whole trucks, so a district
   * offered 0.4 legs a cycle gets two trucks every five. An accumulator rather
   * than a draw: there is no randomness anywhere in this file, and a corridor that
   * rolled dice would be a corridor no governor could plan against (Law 3).
   */
  private pending = 0;

  /**
   * Which ordered pair of interchanges the next leg runs between.
   *
   * A cursor over the `n × (n − 1)` ordered pairs, advanced once per dispatch, so
   * traffic is spread evenly over every route the mayor has built and over both
   * directions of each. Deterministic, and deliberately not a draw — see `pending`.
   */
  private nextPair = 0;

  /**
   * Completed legs already paid for and charged to the roads.
   *
   * The mark idiom (ADR-021 D138): a charge is `total − mark`, so `haulage()`
   * called twice on the same cycle books nothing the second time. The most
   * dangerous field in this snapshot — a district restored with it at zero pays
   * itself a toll on every truck that has ever crossed it and wears its roads to
   * nothing in one cycle, silently, because road quality is one number and nothing
   * announces where it went.
   */
  private settledMark = 0;

  private ledger: FreightLedger = emptyLedger();

  constructor(private readonly grid: TerrainGrid, config: Partial<FreightConfig> = {}) {
    this.config = { ...DEFAULT_FREIGHT_CONFIG, ...config };
  }

  getConfig(): FreightConfig {
    return { ...this.config };
  }

  updateConfig(partial: Partial<FreightConfig>): void {
    this.config = { ...this.config, ...partial };
  }

  // --- The structures --------------------------------------------------------

  /**
   * Build an interchange, or say why not.
   *
   * Both cells have to be road the district already laid. That is the whole of the
   * land cost and it is deliberately not a seizure: an interchange does not seal a
   * cell, destroy a bush or close a portal, because it *is* road — what it changes
   * is that trucks now have a reason to use it. The mayor pays for the corridor in
   * pavement they had already poured and in everything that drives on it afterwards.
   *
   * §5 says *"edge structures"* and this build drops the edge. The map is a torus
   * for movement — `CarManager` wraps in both axes — so its west and east columns
   * are adjacent, and a west-to-east corridor on a district's drawn border would be
   * one cell long. An interchange anywhere is the honest translation, and it is
   * also the more interesting one: §12 promises that *"a well-placed interchange
   * makes the district a corridor"*, and a mayor who picks both ends picks the
   * route. The two cells survive, because a two-cell structure is a layout decision
   * and a pin is not.
   */
  build(x: number, y: number, axis: InterchangeAxis): FreightResult {
    const cells = interchangeCells({ x, y, axis });

    for (const cell of cells) {
      const state = this.grid.getCell(cell.x, cell.y)?.state;
      if (state === undefined) return { ok: false, reason: 'off-map' };
      if (
        state !== CellState.POURED &&
        state !== CellState.CURED &&
        state !== CellState.PAINTED
      ) {
        return { ok: false, reason: 'unpaved' };
      }
      if (this.covers(cell.x, cell.y)) return { ok: false, reason: 'occupied' };
    }

    this.interchanges.push({ id: this.nextId++, x, y, axis });
    this.ledger.interchanges++;
    this.ledger.spent += this.config.INTERCHANGE_COST;
    return { ok: true, cost: this.config.INTERCHANGE_COST };
  }

  /** Whether an interchange already covers this cell. */
  covers(x: number, y: number): boolean {
    for (const structure of this.interchanges) {
      for (const cell of interchangeCells(structure)) {
        if (cell.x === x && cell.y === y) return true;
      }
    }
    return false;
  }

  /** The interchanges, in build order. */
  getInterchanges(): readonly Interchange[] {
    return this.interchanges;
  }

  /**
   * Whether this district is on a corridor.
   *
   * Two interchanges is the whole condition. Whether a truck can actually get from
   * one to the other is a question about the roads, and it is answered the only way
   * §4 allows one to be answered: by driving.
   */
  get connected(): boolean {
    return this.interchanges.length >= 2;
  }

  // --- The national input ----------------------------------------------------

  /** Truck-legs offered per budget cycle. */
  getOffered(): number {
    return this.offered;
  }

  /**
   * Set what the polity routes through this district, in truck-legs per budget
   * cycle. `null` is none.
   *
   * `setPowerAllocation`'s third sibling and the same standing: a national input
   * (§4) that nothing inside the simulation sets, because there is no polity.
   * Negative and non-finite rates are floored at zero — a corridor that ran trucks
   * backwards would be a `NaN` in a leg counter, and there is nothing sensible for
   * it to mean.
   */
  setOffered(legs: number | null): void {
    this.offered = legs !== null && Number.isFinite(legs) ? Math.max(0, legs) : 0;
  }

  // --- The legs --------------------------------------------------------------

  /**
   * The legs due this budget cycle.
   *
   * Called once per settled cycle by `SurfaceSim`, which is the only object that
   * can see both a corridor and a road full of vehicles. Every whole leg the
   * accumulator releases is counted as *offered*; whether it becomes a truck is
   * the caller's answer, and the caller says so with `dispatched` or `lapsed`.
   *
   * A district with fewer than two interchanges lapses everything it is offered,
   * which is the honest record: the polity routed freight at a district that has
   * nowhere for it to enter, and the freight did not move.
   */
  due(): FreightLeg[] {
    this.pending += this.offered;
    const whole = Math.floor(this.pending);
    if (whole <= 0) return [];
    this.pending -= whole;
    this.ledger.legsOffered += whole;

    if (!this.connected) {
      this.ledger.legsLapsed += whole;
      return [];
    }

    const legs: FreightLeg[] = [];
    for (let i = 0; i < whole; i++) {
      const pair = this.pairAt(this.nextPair);
      this.nextPair = (this.nextPair + 1) % this.pairCount();
      legs.push({
        id: this.nextLegId++,
        from: { x: pair.from.x, y: pair.from.y },
        to: { x: pair.to.x, y: pair.to.y },
      });
    }
    return legs;
  }

  /**
   * The next leg id, for a truck this class did not send.
   *
   * §7's construction convoys are lorries on the same roads with the same
   * outcomes, and `CarManager` reports an outcome under a leg id — so there is one
   * id space over everything that drives this district with a manifest, and it is
   * kept here because `nextLegId` is here and is in the snapshot. What a convoy
   * *is* is not this class's business: a delivery earns no toll, appears in no
   * corridor ledger, and is counted by whoever ordered it (`FacilityManager`).
   */
  claimLegId(): number {
    return this.nextLegId++;
  }

  /** A truck entered the map. */
  dispatched(): void {
    this.ledger.legsDispatched++;
  }

  /** A leg the caller could not put a truck on. */
  lapsed(): void {
    this.ledger.legsLapsed++;
  }

  /**
   * A truck reached the far interchange, or gave up.
   *
   * The leg id is not held anywhere here: a leg exists as a truck between dispatch
   * and outcome, and the truck is the record of it. What this counts is how many of
   * each there were.
   */
  outcome(arrived: boolean): void {
    if (arrived) this.ledger.legsCompleted++;
    else this.ledger.legsAbandoned++;
  }

  // --- What a completed leg is worth, and what it costs ----------------------

  /**
   * What the legs completed since the last booking come to.
   *
   * Pure: called twice without a `recordHaulage` between, it answers the same
   * thing twice. `SurfaceSim` books it once per settled budget cycle, in the same
   * breath as the rent and the utility bill.
   */
  haulage(): Haulage {
    const legs = Math.max(0, this.ledger.legsCompleted - this.settledMark);
    return {
      legs,
      tolls: legs * this.config.TOLL_PER_LEG,
      wear: legs * this.config.WEAR_PER_LEG,
    };
  }

  /** Book it: the mark advances, and neither figure is charged twice. */
  recordHaulage(h: Haulage): void {
    if (h.legs <= 0) return;
    this.settledMark += h.legs;
    this.ledger.tollsEarned += h.tolls;
    this.ledger.roadWear += h.wear;
  }

  // --- The record ------------------------------------------------------------

  getLedger(): Readonly<FreightLedger> {
    return this.ledger;
  }

  /** Whether anything has ever happened here. See `canonicalState`. */
  get active(): boolean {
    return this.interchanges.length > 0 || this.ledger.legsOffered > 0;
  }

  // --- State -----------------------------------------------------------------

  save(): FreightSnapshot {
    return {
      interchanges: this.interchanges.map((i) => ({ ...i })),
      nextId: this.nextId,
      nextLegId: this.nextLegId,
      offered: this.offered,
      pending: this.pending,
      nextPair: this.nextPair,
      settledMark: this.settledMark,
      ledger: { ...this.ledger },
    };
  }

  /**
   * Restore from `save()`.
   *
   * `settledMark` matters more here than anything else in the file, for the reason
   * its own comment gives. `pending` and `nextPair` matter for a quieter one: a
   * district restored with either at zero runs its next few legs between different
   * interchanges at slightly different times, which is a different set of ants
   * under a different set of wheels.
   */
  load(state: FreightSnapshot): void {
    this.interchanges.length = 0;
    for (const structure of state.interchanges) this.interchanges.push({ ...structure });
    this.nextId = state.nextId;
    this.nextLegId = state.nextLegId;
    this.offered = state.offered;
    this.pending = state.pending;
    this.nextPair = state.nextPair;
    this.settledMark = state.settledMark;
    this.ledger = { ...state.ledger };
  }

  /**
   * The corridor, for hashing — and nothing at all when there is none.
   *
   * The key is omitted rather than written as a block of zeroes, on the same terms
   * as the Chronicle and the fiscal account: a district that has never built an
   * interchange and never been offered a leg is the same world as one where this
   * class does not exist, and every golden master recorded before freight existed
   * has to keep hashing to what it hashed to.
   */
  canonicalState(): Canonical | undefined {
    if (!this.active) return undefined;
    return {
      interchanges: this.interchanges.map((i) => `${i.id}:${i.x},${i.y}${i.axis}`),
      nextId: this.nextId,
      nextLegId: this.nextLegId,
      offered: this.offered,
      pending: this.pending,
      nextPair: this.nextPair,
      settledMark: this.settledMark,
      ledger: { ...this.ledger },
    };
  }

  // --- Pair selection --------------------------------------------------------

  /** Ordered pairs of distinct interchanges: `n × (n − 1)` of them. */
  private pairCount(): number {
    const n = this.interchanges.length;
    return n * (n - 1);
  }

  /**
   * The `k`th ordered pair, by a fixed enumeration.
   *
   * `k → (i, j)` with `i = ⌊k / (n − 1)⌋` and `j` the `k mod (n − 1)`th index that
   * is not `i`. Total, deterministic, and stable under a reload; it shifts when the
   * mayor builds another interchange, which is a change to the district rather than
   * to the rule.
   */
  private pairAt(k: number): { from: Interchange; to: Interchange } {
    const n = this.interchanges.length;
    const span = n - 1;
    const index = ((k % (n * span)) + n * span) % (n * span);
    const i = Math.floor(index / span);
    const r = index % span;
    const j = r >= i ? r + 1 : r;
    return { from: this.interchanges[i], to: this.interchanges[j] };
  }
}
